/*
 * Copyright Higharc Inc. 2019
 */
import { hslToRgb, rgbToHsl } from "common/utils/colorConversion";
import type { Layers, Scene, Vector3, WebGLRenderer, WebGLRenderTarget } from "three";
import {
  ClampToEdgeWrapping,
  Color,
  CubeTexture,
  DataTexture,
  FloatType,
  LinearFilter,
  LinearSRGBColorSpace,
  Object3D,
  PMREMGenerator,
  RGBAFormat,
  SRGBColorSpace,
  UnsignedByteType,
} from "three";
import { frostedGlassUniformsFromPMREM } from "../../materials/shaders/frostedGlassTransmission";
import { CubeCameraUtil } from "./CubeCameraUtil";
import { IrradianceMapUtil } from "./IrradianceMapUtil";
import type { LayerSpecificUniformList } from "./LayerSpecificUniformList";
import { SH } from "./SH";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";
import type { SHLightProbeVisualizerData } from "./SHLightProbeVisualizer";
import { SHLightProbeVisualizer } from "./SHLightProbeVisualizer";
import "./SphericalHarmonicsShader";
import { TetrahedronCameraUtil } from "./TetrahedronCameraUtil";

export interface UpdateProbeParams {
  renderer: WebGLRenderer;
  scene: Scene;
  near: number;
  far: number;
  bounceIndex: number;
  onBeforeRender: (bounceIndex: number) => void;
  onAfterRender: (bounceIndex: number) => void;
  renderLayers: Layers;
  irradianceSaturationMultiplier: number;
}

function sRGBToLinear(value) {
  if (value <= 0.04045) {
    return value * 0.0773993808;
  }
  return Math.pow(value * 0.9478672986 + 0.0521327014, 2.4);
}

const pmremGeneratorCache = new WeakMap<WebGLRenderer, PMREMGenerator>();

function getPmremGenerator(renderer: WebGLRenderer): PMREMGenerator {
  const pmremGenerator = pmremGeneratorCache.get(renderer);
  if (pmremGenerator) {
    return pmremGenerator;
  }
  const newPmremGenerator = new PMREMGenerator(renderer);
  pmremGeneratorCache.set(renderer, newPmremGenerator);
  return newPmremGenerator;
}

export class SHLightProbe extends Object3D implements SHLightProbeVisualizerData {
  useFloatRenderTarget: boolean;
  skipRender: boolean;
  private shOrderInternal: number | undefined;
  colorSHCoefficients?: Vector3[];
  visualizer: SHLightProbeVisualizer;
  private irradianceCubeMapTextureInternal?: CubeTexture;
  private radianceCubeMapTextureInternal?: CubeTexture;

  // Target resolution
  radianceResolution: number;
  irradianceResolution: number;

  // Size of actual data currently stored
  storedRadianceCubeResolution: number;
  storedIrradianceCubeResolution: number;

  radianceFaces: Float32Array[];
  irradianceFaces: Float32Array[];

  // Prefiltered Mipmapped Radiance Environment Map data
  // https://threejs.org/docs/#PMREMGenerator
  pmrem: WebGLRenderTarget | null = null;
  private pmremUniforms: LayerSpecificUniformList | undefined = undefined;

  /**
   * @param {*} useFloatRenderTarget Set to true to use a float render target to render the probe.
   * @param {*} shOrder Spherical harmonics order. Order 1 has 4 coefficients, order 2 has 9 coefficients. The order may be undefined, in which case only a cube map gets stored.
   * @param {*} radianceResolution
   * @param {*} irradianceResolution
   */
  constructor(
    useFloatRenderTarget: boolean,
    shOrder: number | undefined,
    radianceResolution: number,
    irradianceResolution: number,
    private generatePmrem: boolean = false,
  ) {
    super();

    this.useFloatRenderTarget = useFloatRenderTarget;

    this.skipRender = false;

    this.shOrderInternal = shOrder;

    if (this.shOrderInternal !== undefined) {
      this.colorSHCoefficients = SH.initSHCoefficients(this.shOrderInternal, false);
    }

    this.radianceResolution = radianceResolution;
    this.storedRadianceCubeResolution = radianceResolution;
    this.storedIrradianceCubeResolution = irradianceResolution;
    this.irradianceResolution = irradianceResolution;

    // Cube faces are stored in the standard order: +x, -x, +y, -y, +z, -z.
    this.radianceFaces = [];
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      this.radianceFaces.push(
        new Float32Array(this.storedRadianceCubeResolution * this.storedRadianceCubeResolution * 4),
      );
    }
    this.irradianceFaces = [];
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      this.irradianceFaces.push(
        new Float32Array(this.storedIrradianceCubeResolution * this.storedIrradianceCubeResolution * 4),
      );
    }

    this.visualizer = new SHLightProbeVisualizer(this);
    this.add(this.visualizer.mesh);
  }

  get shOrder(): number | undefined {
    return this.shOrderInternal;
  }
  set shOrder(newValue: number | undefined) {
    if (this.shOrderInternal === newValue) {
      return;
    }
    this.shOrderInternal = newValue;

    if (this.shOrderInternal !== undefined) {
      this.colorSHCoefficients = SH.initSHCoefficients(this.shOrderInternal, false);
    }

    this.visualizer.updateShShader();
    this.visualizer.updateData();
  }

  get irradianceCubeMapTexture(): CubeTexture {
    if (this.irradianceCubeMapTextureInternal === undefined) {
      const faceTextures: any[] = [];
      for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
        faceTextures[faceIndex] = new DataTexture(
          this.irradianceFaces[faceIndex],
          this.storedIrradianceCubeResolution,
          this.storedIrradianceCubeResolution,
        );
      }
      this.irradianceCubeMapTextureInternal = new CubeTexture(
        faceTextures,
        undefined,
        ClampToEdgeWrapping,
        ClampToEdgeWrapping,
        LinearFilter,
        LinearFilter,
        RGBAFormat,
        FloatType, // This is always float, since we'll upload the processed light probe data here.
      );
      this.irradianceCubeMapTextureInternal.flipY = false;
      this.irradianceCubeMapTextureInternal.needsUpdate = true;
    }
    return this.irradianceCubeMapTextureInternal;
  }

  get radianceCubeMapTexture(): CubeTexture {
    if (this.radianceCubeMapTextureInternal === undefined) {
      const faceTextures: any[] = [];
      for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
        faceTextures[faceIndex] = new DataTexture(
          this.radianceFaces[faceIndex],
          this.storedRadianceCubeResolution,
          this.storedRadianceCubeResolution,
        );
      }
      this.radianceCubeMapTextureInternal = new CubeTexture(
        faceTextures,
        undefined,
        ClampToEdgeWrapping,
        ClampToEdgeWrapping,
        LinearFilter,
        LinearFilter,
        RGBAFormat,
        FloatType,
      );
      this.radianceCubeMapTextureInternal.flipY = false;
      this.radianceCubeMapTextureInternal.needsUpdate = true;
    }
    return this.radianceCubeMapTextureInternal;
  }

  resetData() {
    if (this.shOrderInternal !== undefined && this.colorSHCoefficients) {
      for (let i = 0; i < this.getCoefficientVectorCount(); ++i) {
        this.colorSHCoefficients[i].set(0, 0, 0);
      }
    }
    for (let i = 0; i < this.irradianceFaces.length; ++i) {
      const cubeFace = this.irradianceFaces[i];
      for (let dataIndex = 0, pixelCount = cubeFace.length; dataIndex < pixelCount; ++dataIndex) {
        cubeFace[dataIndex] = 0;
      }
    }
    for (let i = 0; i < this.radianceFaces.length; ++i) {
      const cubeFace = this.radianceFaces[i];
      for (let dataIndex = 0, pixelCount = cubeFace.length; dataIndex < pixelCount; ++dataIndex) {
        cubeFace[dataIndex] = 0;
      }
    }
    this.disposeColorCubeMapTextures();
  }

  // Amount of coefficient RGB vectors.
  getCoefficientVectorCount(): number {
    return SH.getCoefficientVectorCount(this.shOrderInternal);
  }

  private updateVisualizerDataIfVisible() {
    if (this.visualizer.mode !== LightProbeVisualizationMode.None) {
      this.visualizer.updateData();
    }
  }

  interpolateFrom(srcProbes: SHLightProbe[]) {
    this.resetData();
    const inverseCount = 1.0 / srcProbes.length;
    for (let probeIndex = 0; probeIndex < srcProbes.length; ++probeIndex) {
      const srcProbe = srcProbes[probeIndex];
      if (this.storedIrradianceCubeResolution !== srcProbe.storedIrradianceCubeResolution) {
        for (let cubeFaceIndex = 0; cubeFaceIndex < 6; ++cubeFaceIndex) {
          this.irradianceFaces[cubeFaceIndex] = new Float32Array(srcProbe.irradianceFaces[0].length);
        }
        this.storedIrradianceCubeResolution = srcProbe.storedIrradianceCubeResolution;
      }
      if (this.shOrderInternal !== undefined && this.colorSHCoefficients && srcProbe.colorSHCoefficients) {
        for (let i = 0; i < this.getCoefficientVectorCount(); ++i) {
          this.colorSHCoefficients[i].addScaledVector(srcProbe.colorSHCoefficients[i], inverseCount);
        }
      }
      for (let cubeFaceIndex = 0; cubeFaceIndex < 6; ++cubeFaceIndex) {
        const srcProbeFace = srcProbe.irradianceFaces[cubeFaceIndex];
        const cubeFace = this.irradianceFaces[cubeFaceIndex];
        for (let dataIndex = 0, dataCount = cubeFace.length; dataIndex < dataCount; ++dataIndex) {
          cubeFace[dataIndex] += srcProbeFace[dataIndex] * inverseCount;
        }
      }
    }
    this.updateVisualizerDataIfVisible();
  }

  // Renders the given scene and stores the resulting cube map (normally an irradiance map). Also converts cube map
  // to SH if SH is enabled.
  *updateFromScene(params: UpdateProbeParams) {
    if (this.shOrderInternal !== undefined && this.colorSHCoefficients) {
      for (let i = 0; i < this.colorSHCoefficients.length; ++i) {
        this.colorSHCoefficients[i].set(0, 0, 0);
      }
    }

    const iterator = this.addRenderedBounce(params);
    while (!iterator.next().done) {
      yield;
    }
    this.disposeColorCubeMapTextures();
    this.updateVisualizerDataIfVisible();
  }

  // This renders a cube map and converts it to the format configured on the probe.
  // In case SH is enabled the final cube map is converted to SH.
  private *addRenderedBounce({
    renderer,
    scene,
    near,
    far,
    bounceIndex,
    onBeforeRender,
    onAfterRender,
    renderLayers,
    irradianceSaturationMultiplier,
  }: UpdateProbeParams) {
    function normalizeColors(readBuffer, readBufferColorSpace) {
      const normalizedBuffer = new Float32Array(readBuffer.length);
      if (readBuffer instanceof Float32Array) {
        normalizedBuffer.set(readBuffer);
      } else if (readBuffer instanceof Uint8Array) {
        for (let i = 0; i < readBuffer.length; ++i) {
          normalizedBuffer[i] = readBuffer[i] / 255.0;
        }
      }
      if (readBufferColorSpace === SRGBColorSpace) {
        // Do sRGB to linear conversion.
        for (let i = 0; i < normalizedBuffer.length; ++i) {
          normalizedBuffer[i] = sRGBToLinear(normalizedBuffer[i]);
        }
      } else if (readBufferColorSpace !== LinearSRGBColorSpace) {
        console.warn("Unexpected color space when reading light probe data. Light probes are in linear color space.");
      }
      return normalizedBuffer;
    }

    const useTetrahedronCam = true;

    const tetraResolution = this.radianceResolution * 2;

    // We render with whatever gamma output setting the renderer has. This way we avoid shader recompiles.
    // sRGB is converted to linear at a later step.
    if (onBeforeRender !== undefined) {
      onBeforeRender(bounceIndex);
    }
    if (useTetrahedronCam) {
      this.radianceFaces = TetrahedronCameraUtil.renderCubeMap(
        renderer,
        scene,
        this,
        near,
        far,
        tetraResolution,
        this.radianceResolution,
        LinearSRGBColorSpace,
        this.useFloatRenderTarget ? FloatType : UnsignedByteType,
        new Color(1.0, 1.0, 1.0),
        1.0,
        normalizeColors,
        renderLayers,
      );
      this.storedRadianceCubeResolution = this.radianceResolution;
    } else {
      const cubeRenderTarget = CubeCameraUtil.renderCubeMap(
        renderer,
        scene,
        this,
        near,
        far,
        this.radianceResolution,
        LinearSRGBColorSpace,
        this.useFloatRenderTarget ? FloatType : UnsignedByteType,
        new Color(1.0, 1.0, 1.0),
        1.0,
        renderLayers,
      );
      this.radianceFaces = CubeCameraUtil.readCubeFaces(
        renderer,
        cubeRenderTarget,
        this.radianceResolution,
        normalizeColors,
      );
      this.storedRadianceCubeResolution = this.radianceResolution;
    }
    if (onAfterRender !== undefined) {
      onAfterRender(bounceIndex);
    }

    yield;

    this.irradianceFaces = this.radianceFaces.map((face) => face.slice());
    this.storedIrradianceCubeResolution = this.storedRadianceCubeResolution;
    // First downsample the cube map so that conversion to irradiance will be faster.
    // We don't lose much quality this way.
    while (this.storedIrradianceCubeResolution > this.irradianceResolution) {
      const targetResolution = Math.max(this.irradianceResolution, this.storedIrradianceCubeResolution / 2);
      this.irradianceFaces = CubeCameraUtil.linearDownsampleCubemap(
        this.irradianceFaces,
        this.storedIrradianceCubeResolution,
        targetResolution,
        4,
      );
      this.storedIrradianceCubeResolution = targetResolution;
    }
    this.irradianceFaces = IrradianceMapUtil.irradianceCubeMapFromRadianceCubeMap(
      this.irradianceFaces,
      this.storedIrradianceCubeResolution,
      4,
    );
    this.irradianceFaces = this.irradianceFaces.map((face) => {
      for (let i = 0; i < face.length; i += 4) {
        const r = face[i];
        const g = face[i + 1];
        const b = face[i + 2];
        const [h, s, l] = rgbToHsl(r, g, b);
        const rgbNew = hslToRgb(h, s * irradianceSaturationMultiplier, l);
        face[i] = rgbNew[0];
        face[i + 1] = rgbNew[1];
        face[i + 2] = rgbNew[2];
      }
      return face;
    });

    if (this.shOrderInternal !== undefined && this.colorSHCoefficients) {
      const bounceShCoefficients = SH.getCoefficientsFromCubeMap(
        this.irradianceFaces,
        this.storedIrradianceCubeResolution,
        this.shOrderInternal,
        4,
        3,
      );
      for (let i = 0; i < this.colorSHCoefficients.length; ++i) {
        this.colorSHCoefficients[i].x += bounceShCoefficients[i][0];
        this.colorSHCoefficients[i].y += bounceShCoefficients[i][1];
        this.colorSHCoefficients[i].z += bounceShCoefficients[i][2];
      }
    }

    if (this.generatePmrem) {
      if (this.storedRadianceCubeResolution < 32) {
        console.error("Radiance cube resolution is too small to generate PMREM. Minimum resolution is 32.");
        this.pmrem?.dispose();
        this.pmrem = null;
        this.pmremUniforms = undefined;
      } else {
        this.pmrem = getPmremGenerator(renderer).fromCubemap(this.radianceCubeMapTexture, this.pmrem);
        this.pmrem.texture.name = this.name;
        this.pmremUniforms = frostedGlassUniformsFromPMREM(this.pmrem);
      }
    }
  }

  willGeneratePmrem(): boolean {
    return this.generatePmrem;
  }

  getPmremUniforms(): LayerSpecificUniformList | undefined {
    return this.pmremUniforms;
  }

  private disposeColorCubeMapTextures() {
    if (this.irradianceCubeMapTextureInternal !== undefined) {
      this.irradianceCubeMapTextureInternal.dispose();
      this.irradianceCubeMapTextureInternal = undefined;
    }
    if (this.radianceCubeMapTextureInternal !== undefined) {
      this.radianceCubeMapTextureInternal.dispose();
      this.radianceCubeMapTextureInternal = undefined;
    }
  }

  dispose() {
    this.disposeColorCubeMapTextures();
    this.pmrem?.dispose();
    this.visualizer.dispose();
  }
}
